import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../config";
import { useTheme } from "../context/ThemeContext";
import { User } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "lucide-react";

const AgentAnalyticsPage = () => {
    const { agentId } = useParams();
    const { darkMode } = useTheme();

    const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const [agentName, setAgentName] = useState("");

    const [dailyData, setDailyData] = useState(null);
    const [monthlyData, setMonthlyData] = useState(null);
    const [allAgents, setAllAgents] = useState([]);
    const params = useParams();
    const initialId = params.agentId;

    const [selectedAgentId, setSelectedAgentId] = useState(initialId);

    const token = localStorage.getItem("token");

    const dateInputClasses = `
    p-2 rounded-lg shadow border w-60
    ${darkMode ? "bg-gray-800 text-white border-gray-600" : "bg-white border-gray-300"}
`;

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    const formatRomanianDate = (iso) => {
        if (!iso) return "";

        const [year, month, day] = iso.split("-");

        const months = [
            "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
            "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
        ];

        return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
    };

    const formatRomanianMonthLabel = (isoMonth) => {
        if (!isoMonth) return "";

        const [year, month] = isoMonth.split("-");

        const months = [
            "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
            "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
        ];

        return `${months[parseInt(month, 10) - 1]} ${year}`;
    };

    const fetchAllAgents = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setAllAgents(data);
        } catch (err) {
            console.error("FETCH USERS ERROR:", err);
        }
    };

    useEffect(() => {
        fetchAllAgents();
    }, []);

    const fetchAgentName = async () => {
        try {
            const token = localStorage.getItem("token");

            const response = await fetch(`${API_BASE_URL}/api/users/${selectedAgentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = await response.json();
            setAgentName(data.name || "Nume necunoscut");
        } catch (err) {
            console.error("AGENT NAME FETCH ERROR:", err);
            setAgentName("Nume necunoscut");
        }
    };

    useEffect(() => {
        fetchAgentName();
    }, [selectedAgentId]);

    const bgCard = darkMode ? "bg-gray-800 text-white" : "bg-white text-black";
    const tableHeader = darkMode ? "bg-gray-700 text-white" : "bg-gray-300 text-black";
    const tableRow = (idx) =>
        darkMode
            ? idx % 2 === 0 ? "bg-gray-800" : "bg-gray-900"
            : idx % 2 === 0 ? "bg-gray-50" : "bg-white";

    // ---------------- DAILY FETCH ----------------
    const fetchDaily = async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/agents/${selectedAgentId}/analytics/daily?date=${date}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = await res.json();
            setDailyData(data);
        } catch (err) {
            console.error("DAILY FETCH ERROR:", err);
        }
    };

    // ---------------- MONTHLY FETCH ----------------
    const fetchMonthly = async () => {
        try {
            const [year, monthNum] = month.split("-");

            const url = `${API_BASE_URL}/api/agents/${selectedAgentId}/analytics/monthly?year=${year}&month=${monthNum}`;

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const text = await res.text();
            const data = JSON.parse(text);
            setMonthlyData(data);
        } catch (err) {
            console.error("MONTHLY FETCH ERROR:", err);
        }
    };

    useEffect(() => { fetchDaily(); }, [date, selectedAgentId]);
    useEffect(() => { fetchMonthly(); }, [month, selectedAgentId]);

    // ---------------- LOADING SCREEN ----------------
    if (!dailyData || !monthlyData) {
        return (
            <div
                className={`flex items-center justify-center min-h-screen text-2xl ${darkMode ? "text-white bg-gray-900" : "text-black bg-gray-100"
                    }`}
            >
                Se încarcă datele…
            </div>
        );
    }

    // ---------------------------------------------------
    // ---------------------- UI --------------------------
    // ---------------------------------------------------
    return (
        <div
            className={`p-6 min-h-screen ${darkMode ? "bg-gray-900 text-white" : "bg-gray-200 text-black"}`}
        >

            <style>
                {`
              input[type="month"]::-webkit-calendar-picker-indicator,
              input[type="date"]::-webkit-calendar-picker-indicator {
                  filter: ${darkMode ? "invert(1)" : "invert(0)"};
              }

              input[type="month"]::-moz-calendar-picker-indicator,
              input[type="date"]::-moz-calendar-picker-indicator {
                  filter: ${darkMode ? "invert(1)" : "invert(0)"};
              }
            `}
            </style>
            <style>
                {`
    .crm-table th,
    .crm-table td {
        text-align: center;
        vertical-align: middle;
        padding: 12px;
        font-size: 15px;
    }

    .crm-table th {
        font-weight: 600;
    }

    /* Fix column widths for perfect alignment */
    .col-small { width: 120px; }
    .col-medium { width: 220px; }
    .col-large { width: 160px; }

    /* Prevent layout jumping */
    .crm-table td, .crm-table th {
        white-space: nowrap;
    }
`}
            </style>

            <h1 className="text-4xl font-bold mb-10">
                Analiză utilizator – {agentName}
            </h1>

            <div className="mb-10">
                <label className="font-semibold block mb-2">Selectează utilizatorul</label>
                <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className={`p-2 rounded-lg shadow border w-60 ${darkMode ? "bg-gray-800 text-white border-gray-600" : "bg-white border-gray-300"
                        }`}
                >
                    {allAgents.map((u) => (
                        <option key={u.id} value={u.id}>
                            {u.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* ---------------- MONTH SELECTOR FIRST ---------------- */}
            <div className="mb-10">
                <label className="font-semibold block mb-1">Alege luna</label>
                <DatePicker
                    selected={new Date(month.replace(/-/g, "/") + "/01")}
                    onChange={(d) => {
                        if (!d) return;
                        setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                    }}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    calendarStartDay={1}
                    portalId="datepicker-root"
                    customInput={
                        <div
                            className={`flex items-center justify-between w-[240px] h-[44px] px-4 
                rounded-xl shadow-sm border-2 transition-all cursor-pointer
                ${darkMode
                                    ? "bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700"
                                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                                }`}
                        >
                            <span className="font-semibold text-sm">
                                {capitalize(
                                    new Date(month + "-01T00:00:00").toLocaleDateString("ro-RO", {
                                        year: "numeric",
                                        month: "long",
                                    })
                                )}
                            </span>

                            <Calendar className={`w-5 h-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`} />
                        </div>
                    }
                />
            </div>


            {/* ------------------------------------------------- */}
            {/* ---------------- MONTHLY KPI CARDS -------------- */}
            {/* ------------------------------------------------- */}
            <h2 className="text-3xl font-bold mb-6">
                Statistici lunare – {formatRomanianMonthLabel(month)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">

                {/* Total Vânzări */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-blue-500/40 text-white" : "bg-blue-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Total vânzări</h3>
                    <p className="text-3xl font-bold mt-2">
                        {monthlyData.salesTotal.toFixed(2)} EUR
                    </p>
                </div>

                {/* Comenzi */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-green-500/40 text-white" : "bg-green-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Comenzi</h3>
                    <p className="text-3xl font-bold mt-2">
                        {monthlyData.ordersCount}
                    </p>
                </div>

                {/* Oferte */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-yellow-400/30 text-white" : "bg-yellow-500 text-black"}`}>
                    <h3 className="text-lg font-semibold">Oferte</h3>
                    <p className="text-3xl font-bold mt-2">
                        {monthlyData.offersCount}
                    </p>
                </div>

                {/* Conversie lunară */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-teal-500/40 text-white" : "bg-teal-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Conversie lunară</h3>
                    <p className="text-3xl font-bold mt-2">
                        {(monthlyData.monthlyConversionRate * 100).toFixed(1)}%
                    </p>
                </div>
            </div>

            {/* ---------------- DAY SELECTOR ---------------- */}
            <div className="mb-12">
                <label className="font-semibold block mb-1">Alege ziua</label>
                <DatePicker
                    selected={new Date(date.replace(/-/g, "/"))}
                    onChange={(d) => {
                        if (!d) return;

                        const local = new Date(Date.UTC(
                            d.getFullYear(),
                            d.getMonth(),
                            d.getDate()
                        ));

                        setDate(local.toISOString().split("T")[0]);
                    }}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={1}
                    portalId="datepicker-root"
                    customInput={
                        <div
                            className={`flex items-center justify-between w-[240px] h-[44px] px-4 
                rounded-xl shadow-sm border-2 transition-all cursor-pointer
                ${darkMode
                                    ? "bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700"
                                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                                }`}
                        >
                            <span className="font-semibold text-sm">
                                {capitalize(
                                    new Date(date.replace(/-/g, "/")).toLocaleDateString("ro-RO", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric"
                                    })
                                )}
                            </span>

                            <Calendar className={`w-5 h-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`} />
                        </div>
                    }
                />
            </div>

            {/* ------------------------------------------------- */}
            {/* ---------------- DAILY KPI CARDS ---------------- */}
            {/* ------------------------------------------------- */}
            <h2 className="text-3xl font-bold mb-6">
                Statistici zilnice – {formatRomanianDate(date)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">

                {/* Vânzări zilnice */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-blue-500/40 text-white" : "bg-blue-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Vânzări în {formatRomanianDate(date)}</h3>
                    <p className="text-3xl font-bold mt-2">
                        {dailyData.dailySales.toFixed(2)} EUR
                    </p>
                </div>

                {/* Comenzi zilnice */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-green-500/40 text-white" : "bg-green-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Comenzi în {formatRomanianDate(date)}</h3>
                    <p className="text-3xl font-bold mt-2">
                        {dailyData.dailyOrdersCount}
                    </p>
                </div>

                {/* Oferte zilnice */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-yellow-400/30 text-white" : "bg-yellow-500 text-black"}`}>
                    <h3 className="text-lg font-semibold">Oferte în {formatRomanianDate(date)}</h3>
                    <p className="text-3xl font-bold mt-2">
                        {dailyData.dailyOffersCount}
                    </p>
                </div>

                {/* Conversie zilnică */}
                <div className={`p-6 rounded-xl shadow 
        ${darkMode ? "bg-teal-500/40 text-white" : "bg-teal-600 text-white"}`}>
                    <h3 className="text-lg font-semibold">Conversie în {formatRomanianDate(date)}</h3>
                    <p className="text-3xl font-bold mt-2">
                        {(dailyData.conversionRate * 100).toFixed(1)}%
                    </p>
                </div>

            </div>

            {/* ------------------------------------------------- */}
            {/* ---------------- DAILY ORDERS TABLE ------------- */}
            {/* ------------------------------------------------- */}
            <h2 className="text-2xl font-bold mb-3 mt-10">
                Comenzi în {formatRomanianDate(date)}
            </h2>

            <div className={`rounded-xl shadow overflow-hidden mb-10 ${bgCard}`}>
                <table className="w-full crm-table">
                    <thead>
                        <tr className={tableHeader}>
                            <th className="border col-small">Nr comandă</th>
                            <th className="border col-medium">Client</th>
                            <th className="border col-large">Total (brut)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dailyData.orders.map((o, idx) => (
                            <tr key={o.orderNumber} className={tableRow(idx)}>
                                <td className="border col-small">{o.orderNumber}</td>
                                <td className="border col-medium">{o.customerName}</td>
                                <td className="border col-large">{o.totalPrice} EUR</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

            </div>

            {/* ------------------------------------------------- */}
            {/* ---------------- DAILY OFFERS TABLE ------------- */}
            {/* ------------------------------------------------- */}
            <h2 className="text-2xl font-bold mb-3">
                Oferte în {formatRomanianDate(date)}
            </h2>

            <div className={`rounded-xl shadow overflow-hidden mb-10 ${bgCard}`}>
                <table className="w-full crm-table">
                    <thead>
                        <tr className={tableHeader}>
                            <th className="border col-small">Nr ofertă</th>
                            <th className="border col-medium">Client</th>
                            <th className="border col-large">Total (brut)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dailyData.offers.map((o, idx) => (
                            <tr key={o.nr} className={tableRow(idx)}>
                                <td className="border col-small">{o.nr}</td>
                                <td className="border col-medium">{o.customerName}</td>
                                <td className="border col-large">{o.total} EUR</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

            </div>

            {/* ------------------------------------------------- */}
            {/* ---------------- MATCHED OFFERS ----------------- */}
            {/* ------------------------------------------------- */}
            <h2 className="text-2xl font-bold mb-3">
                Oferte transformate în comenzi în {formatRomanianDate(date)}
            </h2>

            <div className={`p-6 rounded-xl shadow ${bgCard}`}>
                {dailyData.matchedOffers.length > 0 ? (
                    <ul className="list-disc ml-6 mt-2">
                        {dailyData.matchedOffers.map((m, idx) => (
                            <li key={idx} className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-lg 
    ${darkMode ? "bg-yellow-500/30 text-yellow-200" : "bg-yellow-400 text-black"} font-semibold`}>
                                    Ofertă #{m.offerNr}
                                </span>
                                <span className="text-xl font-bold">→</span>
                                <span className={`px-3 py-1 rounded-lg 
    ${darkMode ? "bg-green-500/40 text-white" : "bg-green-500 text-white"} font-semibold`}>
                                    Comandă #{m.orderNumber}
                                </span>
                                <span className="ml-2 opacity-80">({m.customerName})</span>
                            </li>

                        ))}
                    </ul>
                ) : (
                    <p className="opacity-70">
                        Nu există conversii în {formatRomanianDate(date)}.
                    </p>
                )}
            </div>
        </div>
    );
};

export default AgentAnalyticsPage;
